import { CompassusNavigatorServiceImplFactory } from "../../core/impl/compassusNavigatorServiceImpl";

export const CompassusNavigatorControllerFactory = ({
  compassusNavigatorService,
}: {
  compassusNavigatorService: ReturnType<
    typeof CompassusNavigatorServiceImplFactory
  >;
}) => {
  const getPatients = async ({
    clinicId,
    queryParams,
  }: {
    clinicId: number;
    queryParams?: ICompassusNavigatorQueryParams;
  }) => {
    try {
      const result = await compassusNavigatorService.getPatients({
        clinicId,
        queryParams,
      });
      if (result.success && result.data) {
        return {
          success: true as const,
          data: result.data,
        };
      }
      return {
        success: false as const,
        error: result.error,
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getCounts = async ({
    clinicId,
    queryParams,
  }: {
    clinicId: number;
    queryParams?: Pick<
      ICompassusNavigatorQueryParams,
      "search" | "admission_status" | "acuity_levels"
    >;
  }) => {
    try {
      const result = await compassusNavigatorService.getCounts({
        clinicId,
        queryParams,
      });
      if (result.success && result.data) {
        return {
          success: true as const,
          data: result.data,
        };
      }
      return {
        success: false as const,
        error: result.error,
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPayerFilters = async ({ clinicId }: { clinicId: number }) => {
    try {
      const result = await compassusNavigatorService.getPayerFilters({
        clinicId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getMedicationFilters = async ({ clinicId }: { clinicId: number }) => {
    try {
      const result = await compassusNavigatorService.getMedicationFilters({
        clinicId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getAllergyFilters = async ({ clinicId }: { clinicId: number }) => {
    try {
      const result = await compassusNavigatorService.getAllergyFilters({
        clinicId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getConditionFilters = async ({ clinicId }: { clinicId: number }) => {
    try {
      const result = await compassusNavigatorService.getConditionFilters({
        clinicId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getLastVisitProviderFilters = async ({
    clinicId,
  }: {
    clinicId: number;
  }) => {
    try {
      const result =
        await compassusNavigatorService.getLastVisitProviderFilters({
          clinicId,
        });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getNextVisitProviderFilters = async ({
    clinicId,
  }: {
    clinicId: number;
  }) => {
    try {
      const result =
        await compassusNavigatorService.getNextVisitProviderFilters({
          clinicId,
        });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getTodoTagFilters = async ({ clinicId }: { clinicId: number }) => {
    try {
      const result = await compassusNavigatorService.getTodoTagFilters({
        clinicId,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const getClinicalRiskScoreFilters = async ({
    clinicId,
  }: {
    clinicId: number;
  }) => {
    try {
      const result =
        await compassusNavigatorService.getClinicalRiskScoreFilters({
          clinicId,
        });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const patchPatientTodo = async ({
    clinicId,
    patientId,
    todoTags,
  }: {
    clinicId: number;
    patientId: number;
    todoTags: string[];
  }) => {
    try {
      const result = await compassusNavigatorService.patchPatientTodo({
        clinicId,
        patientId,
        todoTags,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      if (!result.success)
        return { success: false as const, error: result.error };
      return {
        success: false as const,
        error: { message: "No data returned" },
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  const putWatchlist = async ({
    clinicId,
    items,
  }: {
    clinicId: number;
    items: ICompassusWatchlistItem[];
  }) => {
    try {
      const result = await compassusNavigatorService.putWatchlist({
        clinicId,
        items,
      });
      if (result.success && result.data)
        return { success: true as const, data: result.data };
      return {
        success: false as const,
        error: !result.success ? result.error : undefined,
      };
    } catch (error) {
      return {
        success: false as const,
        error: { message: (error as Error).message },
      };
    }
  };

  return {
    getPatients,
    getCounts,
    getPayerFilters,
    getMedicationFilters,
    getAllergyFilters,
    getConditionFilters,
    getLastVisitProviderFilters,
    getNextVisitProviderFilters,
    getTodoTagFilters,
    getClinicalRiskScoreFilters,
    patchPatientTodo,
    putWatchlist,
  };
};
